import React from 'react';
import { XMarkIcon, UsersIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GameOutput } from '../generated-api';

interface SelectedGameCardProps {
  game: GameOutput;
  gameExtensions: string[];
  onChangeGame: () => void;
  onToggleExtension: (extension: string) => void;
  onEditClick?: () => void;
  readOnly?: boolean;
}

const SelectedGameCard: React.FC<SelectedGameCardProps> = ({
  game,
  gameExtensions,
  onChangeGame,
  onToggleExtension,
  onEditClick,
  readOnly = false,
}) => {
  const teamCountText = game.minTeams && game.maxTeams
    ? game.minTeams === game.maxTeams
      ? `${game.minTeams} players`
      : `${game.minTeams}-${game.maxTeams} players`
    : null;

  return (
    <div className="bg-gradient-to-r from-primary-50 to-indigo-50 rounded-xl p-4 border border-primary-100 w-full">
      <div className="flex items-start space-x-4">
        {/* Box Art */}
        <div className="flex-shrink-0">
          {game.imageUrl ? (
            <img
              src={game.imageUrl}
              alt={game.name}
              className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover shadow-md"
            />
          ) : (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-surface-skeleton flex items-center justify-center shadow-md">
              <span className="text-4xl">🎲</span>
            </div>
          )}
        </div>

        {/* Game Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <h3 className="text-lg font-semibold text-heading truncate">
                {game.name}
              </h3>

              {/* Player count badge */}
              {teamCountText && (
                <div className="flex items-center mt-1 text-sm text-body">
                  <UsersIcon className="w-4 h-4 mr-1 text-primary-500" />
                  <span>{teamCountText}</span>
                </div>
              )}

              {/* Tags */}
              {game.tags && game.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {game.tags.slice(0, 4).map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface text-indigo-700 border border-indigo-200"
                    >
                      {tag}
                    </span>
                  ))}
                  {game.tags.length > 4 && (
                    <span className="text-xs text-muted self-center">
                      +{game.tags.length - 4}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1">
              {onEditClick && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditClick();
                  }}
                  data-testid="edit-game-button"
                  className="flex-shrink-0 p-1.5 text-disabled hover:text-body hover:bg-surface rounded-full transition-colors"
                  title="Edit game"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={onChangeGame}
                  data-testid="change-game-button"
                  className="flex-shrink-0 p-1.5 text-disabled hover:text-body hover:bg-surface rounded-full transition-colors"
                  title="Change game"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Extensions - hide entirely in read-only mode when none are selected */}
      {game.availableExtensions && game.availableExtensions.length > 0 && !(readOnly && gameExtensions.length === 0) && (
        <div className="mt-4 pt-3 border-t border-primary-100">
          <label className="block text-xs font-medium text-body mb-2">
            Expansions Used
          </label>
          <div className="flex flex-wrap gap-2">
            {game.availableExtensions.map((extension) => (
              <button
                data-track="game_extension_toggle"
                key={extension}
                type="button"
                onClick={readOnly ? undefined : () => onToggleExtension(extension)}
                disabled={readOnly}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  gameExtensions.includes(extension)
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : readOnly
                    ? 'bg-surface-subtle text-disabled cursor-not-allowed'
                    : 'bg-surface text-body border border-edge hover:border-indigo-300 hover:bg-indigo-50'
                }`}
              >
                {extension}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedGameCard;
